import React, { useState } from "react";
import eyeShow from "../../Assets/Icons/eyeShow.svg";
import eyeHide from "../../Assets/Icons/eyeHide.svg";
import fundraiserIcon from "../../Assets/Icons/FundraiserIcon.svg";
import merchantIcon from "../../Assets/Icons/merchantIcon.svg";
import memberIcon from "../../Assets/Icons/userIcon.svg";

const LinkedAccountPasswordModal = ({
  email,
  roles = [],
  targetRole,
  onVerify,
  onForgotPassword,
  onUseDifferentEmail,
}) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);
    try {
      await onVerify(password);
    } catch (verificationError) {
      setError(
        verificationError?.message || "Unable to verify your password.",
      );
    } finally {
      setLoading(false);
    }
  };

  const roleLabels = {
    member: "Member",
    merchant: "Merchant",
    fundraiser: "Fundraiser",
  };
  const roleBadgeClasses = {
    member: "bg-[#EAF2FF] text-[#00ADEF]",
    merchant: "bg-[#FFF5E6] text-[#C27A00]",
    fundraiser: "bg-[#E9EAEC] text-[#252A31]",
  };
  const roleIcons = {
    member: { src: memberIcon, filter: "none" },
    merchant: {
      src: merchantIcon,
      filter:
        "brightness(0) saturate(100%) invert(49%) sepia(77%) saturate(1260%) hue-rotate(4deg) brightness(91%) contrast(101%)",
    },
    fundraiser: {
      src: fundraiserIcon,
      filter:
        "brightness(0) saturate(100%) invert(15%) sepia(8%) saturate(915%) hue-rotate(178deg) brightness(94%) contrast(91%)",
    },
  };
  const targetRoleLabel = roleLabels[targetRole] || "account";

  return (
    <div className="fixed inset-0 z-[9999] flex min-h-screen items-center justify-center overflow-y-auto bg-black/50 px-4 max-[767px]:items-end max-[767px]:px-0">
      <form
        onSubmit={handleSubmit}
        className="relative w-full max-w-[440px] rounded-[20px] bg-white px-6 py-6 font-poppins shadow-[0_12px_36px_rgba(0,0,0,0.18)] max-[767px]:max-h-[90vh] max-[767px]:overflow-y-auto max-[767px]:overscroll-contain max-[767px]:max-w-none max-[767px]:rounded-b-none max-[767px]:rounded-t-[24px] max-[767px]:px-6 max-[767px]:pb-[calc(1.5rem+env(safe-area-inset-bottom))]"
        role="dialog"
        aria-modal="true"
        aria-labelledby="linked-account-title"
      >
        <button
          type="button"
          onClick={onUseDifferentEmail}
          aria-label="Close and use a different email"
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center text-[24px] leading-none text-[#8C8C8C]"
        >
          &times;
        </button>
        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-[#E8F3FF] text-[#00ADEF]">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M10 13.5a4.5 4.5 0 006.36 0l3-3A4.5 4.5 0 0013 4.14l-1.72 1.72M14 10.5a4.5 4.5 0 00-6.36 0l-3 3A4.5 4.5 0 0011 19.86l1.72-1.72"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        <h2
          id="linked-account-title"
          className="font-poppins text-[18px] font-semibold text-[#303030]"
        >
          You already have a Swoodle account
        </h2>
        <p className="mt-2 break-all font-poppins text-[12px] leading-5 text-[#767676]">
          {email} is already registered as:
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {roles.map((role) => (
            <span
              key={role}
              className={`inline-flex items-center gap-1 rounded-full px-3 py-1 font-poppins text-[12px] font-medium ${
                roleBadgeClasses[role] || "bg-[#E9EAEC] text-[#252A31]"
              }`}
            >
              {roleIcons[role] && (
                <img
                  src={roleIcons[role].src}
                  alt=""
                  aria-hidden="true"
                  className="h-3 w-3 shrink-0 object-contain"
                  style={{ filter: roleIcons[role].filter }}
                />
              )}
              {roleLabels[role] || role}
            </span>
          ))}
        </div>
        <p className="mt-3 font-poppins text-[12px] leading-[18px] text-[#767676]">
          This email already has a Swoodle account. Enter its password to
          continue creating your {targetRoleLabel} account.
        </p>

        <label
          className="mt-3 block font-poppins text-[12px] font-medium text-[#303030]"
          htmlFor="linked-account-password"
        >
          Swoodle password
        </label>
        <div className="mt-1 flex h-[40px] items-center rounded-[9px] border border-[#E1E5EA] px-3">
          <input
            id="linked-account-password"
            autoFocus
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            className="h-full min-w-0 flex-1 border-0 bg-transparent font-poppins text-[13px] outline-none"
            aria-invalid={Boolean(error)}
          />
          <button
            type="button"
            onClick={() => setShowPassword((visible) => !visible)}
            className="ml-2 flex h-7 w-7 items-center justify-center text-[#8C9AA8]"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            <img
              src={showPassword ? eyeShow : eyeHide}
              alt=""
              aria-hidden="true"
              className="h-5 w-5 object-contain"
            />
          </button>
        </div>
        {error && (
          <p className="mt-2 font-poppins text-[13px] text-red-600" role="alert">
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={onForgotPassword}
          className="mt-3 font-poppins text-[12px] font-medium text-[#00ADEF]"
        >
          Forgot password?
        </button>
        <div className="mt-4 flex gap-2 max-[767px]:flex-col">
          <button
            type="button"
            onClick={onUseDifferentEmail}
            className="h-[40px] min-h-[40px] flex-1 rounded-[9px] border border-[#E1E5EA] font-poppins text-[12px] font-medium text-[#303030] hover:bg-[#F8FAFC] max-[767px]:w-full max-[767px]:flex-none"
          >
            Use a different email
          </button>
          <button
            type="submit"
            disabled={!password || loading}
            className="h-[40px] min-h-[40px] flex-1 rounded-[9px] bg-[#00ADEF] font-poppins text-[12px] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60 max-[767px]:w-full max-[767px]:flex-none"
          >
            {loading ? "Verifying..." : `Add ${targetRoleLabel} account`}
          </button>
        </div>
      </form>
    </div>
  );
};

export default LinkedAccountPasswordModal;
